import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, ArrowRight, Clock, User } from "lucide-react";
import styled from "styled-components";
import SessionCardItem from "../components/SessionCardItem";
import headerCreditsIcon from "../assets/header-credits-icon.svg";
import SessionPopup from "../components/SessionPopup";
import CalendarStrip, { CalendarDay } from "../components/CalendarStrip";
import { getTrainerProfile, getTrainerBookings } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { utcTimeOfDayToLocal, formatTimeSlot } from "../utils/timeUtils";
import { parseSlotToLocalTime } from "../utils/utcTimeUtils";

/* ─────────────────────────────
   Types (same as your file)
────────────────────────────── */
interface SessionCard {
  id: string;
  date: string;
  day: string;
  time: string;
  duration: string;
  location: { type: string; address: string };
  student: {
    name: string;
    age: string;
    gender: string;
    level: string;
    preferences: { gloves: string; gloveSize: string; handWraps: string };
  };
  notes: string;
}
interface UpcomingSession {
  id: string;
  month: string;
  day: string;
  time: string;
  student: string;
  gender: string;
  countdown?: string | null; // <-- allow missing or null
  status: string;
  startDateTime?: string | Date;
  endDateTime?: string | Date;
  location?: string;
  address?: string;
  notes?: string;
}

interface TimeSlot {
  id: number;
  time: string;
  status: "available" | "unavailable";
  action: "edit" | "arrow";
}
interface DayTimeSlots {
  [key: number]: TimeSlot[];
}

/* ─────────────────────────────
   Layout wrappers (assume 400px shell from AppLayout)
────────────────────────────── */

const PageScroller = styled.div`
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  /* optional: make the base white so no dark peeks through anywhere */
  background: #ffffff;
`;

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* make sure the container itself spans the viewport */
  min-height: 100vh;
  box-sizing: border-box;
`;

/* ─────────────────────────────
   Header
────────────────────────────── */

const HeaderSection = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 48px;
  padding: 28px 20px 40px;
  padding-top: calc(28px + max(var(--safe-area-top), var(--app-top-inset, 0px)));
  width: 100%;
  background: linear-gradient(157.07deg, #3a3a3a 0%, #252525 81.65%);
  flex-shrink: 0; /* <- don't shrink the header */
`;

const ContentSection = styled.section`
  width: 100%;
  background: #ffffff;
  padding: 20px 20px 40px;
  /* <- this makes the white area fill remaining height */
  flex: 1 0 auto;
`;

// SVG background image for header (replaces gradient block)
const HeaderVectorImg = styled.img`
  position: absolute;
  right: 0;
  top: 0;
  // width: 174px;
  // height: 226px;
  object-fit: contain;
  z-index: 0;
`;

const RowBetween = styled.div`
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const UserLink = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
`;

const Avatar = styled.div`
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: #252525;
  border: 1px solid rgba(114, 114, 114, 0.6);
`;

const UserName = styled.div`
  font-family: Manrope, sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  color: #f7f7f7;
`;

/* ─────────────────────────────
   Stats + Upcoming
────────────────────────────── */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  z-index: 1;
`;

const SessionsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SessionsRow = styled.div`
  display: flex;
  align-items: center; /* align bottoms of number and label */
  gap: 8px;
`;

const SessionsCount = styled.div`
  font-family: "Cooper Hewitt", Manrope, sans-serif;
  font-style: italic; /* Heavy Italic */
  font-weight: 714;
  font-size: 40px;
  // line-height: 120%;
  // letter-spacing: -1px;
  text-transform: uppercase;
  color: #fa403f;
`;

const SessionsWord = styled.div`
  font-family: "Cooper Hewitt", Manrope, sans-serif;
  font-style: italic; /* Heavy Italic */
  font-weight: 714;
  font-size: 20px; /* decreased per spec */
  // line-height: 120%;
  letter-spacing: -1px;
  text-transform: uppercase;
  color: #ffffff;
  margin-top: 11px;
`;

const SessionsLabel = styled.div`
  font-family: Manrope, sans-serif;
  font-weight: 700;
  font-size: 14px; /* normal sentence case, 14px */
  line-height: 16px;
  color: #b0b0b0;
`;

const UpcomingWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UpcomingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const UpcomingTitle = styled.div`
  font-family: Manrope, sans-serif;
  font-weight: 800;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #b0b0b0;
`;

const ViewAll = styled.button`
  background: none;
  border: 0;
  padding: 0;
  font-family: Manrope, sans-serif;
  font-weight: 800;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #e6f0ff;
  cursor: pointer;
`;

const UpcomingScroller = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  overflow-x: auto;
  padding-bottom: 4px;
  align-items: flex-start; /* ← key: don't stretch shorter cards */
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

/* ─────────────────────────────
   Content section
────────────────────────────── */

const SectionTitle = styled.div`
  font-family: Manrope, sans-serif;
  font-weight: 800;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #b0b0b0;
  margin-bottom: 20px;
`;

const TimeSlots = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 20px;
`;

const TimeSlotCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  background: #ffffff;
  border: 1px solid #dedede;
  padding: 0 12px 0 16px;
`;

const TimeSlotInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Dot = styled.div<{ status: "available" | "unavailable" }>`
  width: 8px;
  height: 8px;
  transform: rotate(-45deg);
  background: ${(p) => (p.status === "available" ? "#3c8826" : "#b24025")};
`;

const TimeSlotTime = styled.div`
  font-family: Manrope, sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: #2d2d2d;
`;

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: #d62422;
  border: none;
  cursor: pointer;

  &:hover {
    background: #c02120;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 0;
  color: #717171;
  font-family: Manrope, sans-serif;
  font-weight: 600;
`;




/* ─────────────────────────────
   Component
────────────────────────────── */
const TrainerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trainerProfile, setTrainerProfile] = useState<any>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch trainer profile and bookings
  useEffect(() => {
    const fetchTrainerData = async () => {
      console.log('TrainerDashboard - user object:', user);
      console.log('TrainerDashboard - user.id:', user?.id);
      console.log('TrainerDashboard - user keys:', user ? Object.keys(user) : 'user is null');
      console.log('TrainerDashboard - user._id:', user?._id);
      console.log('TrainerDashboard - user.userId:', user?.userId);

      // Wait for user to be available - check for any possible ID field
      const userId = user?.id || user?._id || user?.userId;
      if (!userId) {
        console.log('TrainerDashboard - No user ID found, waiting for user to be available');
        setLoading(true);
        return;
      }

      console.log('TrainerDashboard - Using userId:', userId);

      try {
        setLoading(true);
        setError(null);

        // Fetch trainer profile and bookings in parallel
        const [profile, bookingsData] = await Promise.all([
          getTrainerProfile(userId),
          getTrainerBookings(userId)
        ]);

        setTrainerProfile(profile);
        setBookings(bookingsData.bookings || []);

        console.log('TrainerDashboard - Profile data:', profile);
        console.log('TrainerDashboard - Availability slots:', profile?.availabilitySlots);
        console.log('TrainerDashboard - Raw bookings data:', bookingsData.bookings);

        // Process bookings to create upcoming sessions
        const now = new Date();
        const upcoming = bookingsData.bookings
          ?.filter((booking: any) => {
            const startTime = new Date(booking.startDateTime);
            return startTime > now && (booking.status === 'confirmed' || booking.status === 'pending');
          })
          .map((booking: any, index: number) => {
            const startDate = new Date(booking.startDateTime);
            const endDate = new Date(booking.endDateTime);

            // Format date (e.g., "AUG 12")
            const dateStr = startDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            }).toUpperCase();

            // Format time (e.g., "10:00 AM - 11:00 AM")
            const timeStr = `${startDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })} - ${endDate.toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}`;

            // Only calculate countdown for the first session (index 0)
            let countdown = null;
            if (index === 0) {
              const timeUntil = startDate.getTime() - now.getTime();
              const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24));
              const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
              const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));

              if (days > 0) {
                countdown = `${days}d ${hours}h ${minutes}m`;
              } else if (hours > 0) {
                countdown = `${hours}h ${minutes}m`;
              } else {
                countdown = `${minutes}m`;
              }
            }

            return {
              id: booking._id,
              month: dateStr.split(' ')[0],
              day: dateStr.split(' ')[1],
              time: timeStr,
              student: booking.userId?.name || 'Unknown Student',
              gender: booking.userId?.sex || 'Unknown',
              countdown: countdown,
              status: booking.status,
              // Include full booking data for session details
              startDateTime: booking.startDateTime,
              endDateTime: booking.endDateTime,
              location: booking.location || 'HOME',
              address: booking.address || '',
              notes: booking.notes || '',
              userId: booking.userId,
            };
          }) || [];

        setUpcomingSessions(upcoming);
        console.log('TrainerDashboard - Processed upcoming sessions:', upcoming);

        // Generate calendar days for the next 7 days
        const generateNext7Days = () => {
          const days = [];
          const today = new Date();

          for (let i = 0; i < 7; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);

            const month = date.toLocaleDateString('en-US', { month: 'short' });
            const day = date.getDate().toString();
            const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();

            days.push({
              id: i + 1,
              month,
              day,
              dayName,
              isSelected: i === 0
            });
          }

          return days;
        };

        setCalendarDays(generateNext7Days());

        // Generate time slots from backend availabilitySlots (no calculations needed)
        const generateTimeSlotsFromAvailability = () => {
          const slots: DayTimeSlots = {};

          if (!profile?.availabilitySlots) {
            console.log('TrainerDashboard - No availability slots from backend, returning empty slots');
            return slots;
          }

          console.log('TrainerDashboard - Using backend availabilitySlots:', profile.availabilitySlots);

          // Generate next 7 days dates (matching backend format)
          // Use local date components to avoid timezone shifts from toISOString()
          const today = new Date();
          const days = [];
          for (let i = 0; i < 7; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            // Format as YYYY-MM-DD using local date components (not UTC)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            days.push({ dayId: i + 1, dateStr });
          }

          console.log('TrainerDashboard - Generated date strings:', days.map(d => d.dateStr));

          // Process each day
          days.forEach(({ dayId, dateStr }) => {
            // Get slots for this day from backend (already calculated)
            const daySlots = profile.availabilitySlots[dateStr] || [];

            // Convert UTC ISO slots to local time for display
            const convertedSlots: TimeSlot[] = daySlots.map((slot: any, index: number) => ({
              id: index + 1,
              time: parseSlotToLocalTime(slot.slot), // Convert UTC to local time for display
              status: slot.available ? ('available' as const) : ('unavailable' as const),
              action: 'edit' as const
            }));

            slots[dayId] = convertedSlots;
            console.log(`TrainerDashboard - Slots for ${dateStr} (day ${dayId}):`, convertedSlots);
          });

          return slots;
        };

        setDayTimeSlots(generateTimeSlotsFromAvailability());

      } catch (err) {
        console.error('Error fetching trainer data:', err);
        setError('Failed to load trainer data');
      } finally {
        setLoading(false);
      }
    };

    fetchTrainerData();
  }, [user?.id, user?._id, user?.userId]); // Depend on any possible ID field

  const [pendingSessions, setPendingSessions] = useState<SessionCard[]>([]);

  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [dayTimeSlots, setDayTimeSlots] = useState<DayTimeSlots>({});
  const [selectedDayId, setSelectedDayId] = useState(1);

  // Calculate session statistics
  const totalSessions = bookings.length;
  const upcomingSessionsCount = upcomingSessions.length;
  const pendingSessionsCount = bookings.filter(booking => booking.status === 'pending').length;

  const handleDaySelect = (dayId: number) => {
    setSelectedDayId(dayId);
    setCalendarDays((prev) =>
      prev.map((d) => ({ ...d, isSelected: d.id === dayId }))
    );
  };

  const currentTimeSlots = dayTimeSlots[selectedDayId] || [];
  const nextUpcoming = upcomingSessions[0];
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [currentSessionIndex, setCurrentSessionIndex] = useState(0);

  const handleAcknowledge = (sessionId: string) => {
    // Update the booking status to confirmed
    setBookings(prev =>
      prev.map(booking =>
        booking._id === sessionId
          ? { ...booking, status: 'confirmed' }
          : booking
      )
    );

    // Remove from pending sessions
    setPendingSessions(prev => {
      const next = prev.filter(s => s.id !== sessionId);
      if (next.length === 0) setShowSessionsModal(false);
      else setCurrentSessionIndex(i => Math.min(i, next.length - 1));
      return next;
    });

    // Refresh upcoming sessions to reflect the change
    const now = new Date();
    const updatedUpcoming = bookings
      .map(booking => booking._id === sessionId ? { ...booking, status: 'confirmed' } : booking)
      .filter((booking: any) => {
        const startTime = new Date(booking.startDateTime);
        return startTime > now && (booking.status === 'confirmed' || booking.status === 'pending');
      })
      .map((booking: any) => {
        const startDate = new Date(booking.startDateTime);
        const endDate = new Date(booking.endDateTime);

        const dateStr = startDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        }).toUpperCase();

        const timeStr = `${startDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })} - ${endDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })}`;

        const timeUntil = startDate.getTime() - now.getTime();
        const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));

        let countdown = '';
        if (days > 0) {
          countdown = `${days}d ${hours}h ${minutes}m`;
        } else if (hours > 0) {
          countdown = `${hours}h ${minutes}m`;
        } else {
          countdown = `${minutes}m`;
        }

        return {
          id: booking._id,
          month: dateStr.split(' ')[0],
          day: dateStr.split(' ')[1],
          time: timeStr,
          student: booking.userId?.name || 'Unknown Student',
          gender: booking.userId?.sex || '',
          countdown: countdown,
          status: booking.status
        };
      });

    setUpcomingSessions(updatedUpcoming);
  };

  const handleCancel = (sessionId: string) => {
    // Update the booking status to cancelled
    setBookings(prev =>
      prev.map(booking =>
        booking._id === sessionId
          ? { ...booking, status: 'cancelled' }
          : booking
      )
    );

    // Remove from pending sessions
    setPendingSessions(prev => {
      const next = prev.filter(s => s.id !== sessionId);
      if (next.length === 0) setShowSessionsModal(false);
      else setCurrentSessionIndex(i => Math.min(i, next.length - 1));
      return next;
    });

    // Refresh upcoming sessions to reflect the change
    const now = new Date();
    const updatedUpcoming = bookings
      .map(booking => booking._id === sessionId ? { ...booking, status: 'cancelled' } : booking)
      .filter((booking: any) => {
        const startTime = new Date(booking.startDateTime);
        return startTime > now && (booking.status === 'confirmed' || booking.status === 'pending');
      })
      .map((booking: any) => {
        const startDate = new Date(booking.startDateTime);
        const endDate = new Date(booking.endDateTime);

        const dateStr = startDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        }).toUpperCase();

        const timeStr = `${startDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })} - ${endDate.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        })}`;

        const timeUntil = startDate.getTime() - now.getTime();
        const days = Math.floor(timeUntil / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeUntil % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeUntil % (1000 * 60 * 60)) / (1000 * 60));

        let countdown = '';
        if (days > 0) {
          countdown = `${days}d ${hours}h ${minutes}m`;
        } else if (hours > 0) {
          countdown = `${hours}h ${minutes}m`;
        } else {
          countdown = `${minutes}m`;
        }

        return {
          id: booking._id,
          month: dateStr.split(' ')[0],
          day: dateStr.split(' ')[1],
          time: timeStr,
          student: booking.userId?.name || 'Unknown Student',
          gender: booking.userId?.sex || '',
          countdown: countdown,
          status: booking.status
        };
      });

    setUpcomingSessions(updatedUpcoming);
  };

  return (
    <PageScroller>
      <DashboardContainer>
        {/* Header */}
        <HeaderSection>
          <HeaderVectorImg src={headerCreditsIcon} alt="" aria-hidden="true" />

          <RowBetween>
            <UserLink onClick={() => navigate("/trainer/profile")}>
              <Avatar>
                <User size={21} color="#717171" />
              </Avatar>
              <UserName>
                {loading ? 'Loading...' :
                  error ? 'Error' :
                    trainerProfile?.name || 'Trainer'}
              </UserName>
            </UserLink>

            {/* spacer icon placeholder for symmetry */}
            <Avatar style={{ opacity: 0 }} />
          </RowBetween>

          <Stack>
            <SessionsBlock>
              <SessionsRow>
                <SessionsCount>
                  {loading ? '...' :
                    error ? '0' :
                      upcomingSessionsCount}
                </SessionsCount>
                <SessionsWord>SESSIONS</SessionsWord>
              </SessionsRow>
              <SessionsLabel>Upcoming</SessionsLabel>
            </SessionsBlock>

            {!loading && upcomingSessions.length > 0 && (
              <UpcomingWrap>
                <UpcomingHeader>
                  <UpcomingTitle>
                    <span style={{ color: '#B0B0B0' }}>//</span>{' '}
                    <span style={{ color: '#717171' }}>Upcoming Sessions</span>
                  </UpcomingTitle>
                  <ViewAll onClick={() => navigate("/trainer/sessions")}>
                    View All
                  </ViewAll>
                </UpcomingHeader>

                <UpcomingScroller>
                  {upcomingSessions.map((s) => (
                    <SessionCardItem
                      key={s.id}
                      month={s.month}
                      day={s.day}
                      time={s.time}
                      participant={`${s.student}${s.gender ? `, ${s.gender}` : ""}`}
                      countdown={s.countdown}      // shows bar if present
                      layout="row"
                      onClick={() => {
                        // Find the original booking data from the bookings array
                        const originalBooking = bookings.find((b: any) => b._id === s.id);
                        navigate("/trainer/session-details", {
                          state: {
                            session: {
                              ...s,
                              startDateTime: originalBooking?.startDateTime || s.startDateTime || '',
                              endDateTime: originalBooking?.endDateTime || s.endDateTime || '',
                              location: originalBooking?.location || s.location || 'HOME',
                              address: originalBooking?.address || s.address || 'Address not specified',
                              sessionIn: s.countdown,
                              studentName: s.student,
                              studentAge: originalBooking?.userId?.age?.toString() || '',
                              studentGender: s.gender,
                              studentLevel: originalBooking?.userId?.experience || 'BEGINNER',
                              studentCountryCode: originalBooking?.userId?.countryCode || '',
                              studentMobileNumber: originalBooking?.userId?.mobileNumber || '',
                              notes: originalBooking?.notes || s.notes || '',
                              requirements: {
                                gloves: originalBooking?.userId?.hasGloves ? 'YES' : 'NO',
                                gloveSize: (() => {
                                  const sizeMap: Record<string, string> = { '8': 'XS', '10': 'S', '12': 'M', '14': 'L' };
                                  const val = originalBooking?.userId?.gloveSize?.toString() || '';
                                  return sizeMap[val] || val;
                                })(),
                                handWraps: originalBooking?.userId?.hasHandwrap ? 'YES' : 'NO',
                              },
                            }
                          }
                        });
                      }}
                    />
                  ))}
                </UpcomingScroller>
              </UpcomingWrap>
            )}
          </Stack>
        </HeaderSection>

        {/* Content */}
        <ContentSection>
          <SectionTitle>// your calendar</SectionTitle>

          <CalendarStrip
            days={calendarDays}
            onDaySelect={handleDaySelect}
            loading={loading}
            emptyMessage="No calendar data available"
          />

          {loading ? (
            <div style={{ color: '#717171', padding: '20px' }}>Loading time slots...</div>
          ) : currentTimeSlots.length === 0 ? (
            <EmptyState>
              <div>No time slots for this day</div>
              <ActionButton
                onClick={() => navigate("/trainer/edit-availability")}
                aria-label="Add availability"
              >
                <Edit size={20} color="#FFFFFF" />
              </ActionButton>
            </EmptyState>
          ) : (
            <TimeSlots>
              {currentTimeSlots.map((slot) => (
                <TimeSlotCard key={slot.id}>
                  <TimeSlotInfo>
                    <Dot status={slot.status} />
                    <TimeSlotTime>{slot.time}</TimeSlotTime>
                  </TimeSlotInfo>
                  <ActionButton
                    onClick={() => navigate("/trainer/edit-availability")}
                    aria-label={slot.action === "edit" ? "Edit Slot" : "Go To Slot"}
                  >
                    {slot.action === "edit" ? (
                      <Edit size={20} color="#FFFFFF" />
                    ) : (
                      <ArrowRight size={20} color="#FFFFFF" />
                    )}
                  </ActionButton>
                </TimeSlotCard>
              ))}
            </TimeSlots>
          )}
        </ContentSection>
        {showSessionsModal && pendingSessions.length > 0 && (
          <SessionPopup
            sessions={pendingSessions}
            startIndex={0}
            onAcknowledge={handleAcknowledge}
            onCancel={handleCancel}
            onClose={() => setShowSessionsModal(false)}
          />
        )}

      </DashboardContainer>
    </PageScroller>
  );
};

export default TrainerDashboard;
